/**
 * Breakdown menu: multi-screen menu to select event+prop pair
 */
import {renameEvent} from 'common/report/util/events';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {propagateEvent} from 'common/util/dom';

import {ChildMenu} from '../child-menu';
import {EventsMenu} from '../events-menu/types';
import {IPropertiesMenu} from '../properties-menu/types';
import {template} from './template';

interface BreakdownMenuState {
  staged: {
    event: CustomEvent;
  };
}

export class BreakdownMenu extends ChildMenu<BreakdownMenuState> {
  private childMenu: EventsMenu | IPropertiesMenu;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`],
        default: `none`,
        description: `whether to hide custom properties from properties menus`,
      },
    };
  }

  get config() {
    return {
      template,

      defaultState: {
        staged: null,
      },

      helpers: {
        ...super.config.helpers,
        onMenuInserted: (vnode) => {
          requestAnimationFrame(() => {
            this.childMenu = vnode.elm;
            // Trigger a render for items dependent on childMenu
            this.update();
          });
        },
        getSelectedEvent: () => this.getSelectedEvent(),
        getSelectedProperty: () => this.attr(`selected`)?.property || null,
        handleSelectEvent: (ev) => {
          this.update({staged: {event: ev.detail.item}, searchFilter: ``});
        },
        propagateEvent: (ev) => propagateEvent(this, ev),
        handleSelectProperty: (ev) => {
          const property = ev.detail.item;
          this.dispatchEvent(
            new CustomEvent(`select`, {
              detail: {
                item: {
                  event: this.getSelectedEvent(),
                  property,
                },
              },
            }),
          );
        },
        /** Since mp-properties-menu defines when content is searchable, trigger a manual render */
        handleStagedProperty: () => this.update(),
      },
    };
  }

  public isContentSearchable() {
    return this.childMenu.isContentSearchable();
  }

  public getSelectedEvent() {
    // depending on whether this is one part of a compound menu
    let selected = this.state.staged?.event || this.attr(`selected`);
    if (selected && selected.event) {
      selected = selected.event;
    }
    return selected;
  }

  public getSelectedEventName() {
    const selected = this.getSelectedEvent();
    return selected && (selected.custom ? selected.name || selected.label : selected.value);
  }

  selectedLabel() {
    const evName = renameEvent(this.getSelectedEventName());
    if (evName) {
      return [evName, `by`, this.getSelectedPropertyName() || `...`].join(` `);
    } else {
      return `Select event...`;
    }
  }
}

export const BreakdownMenuEl = defineAndMakeJsxFactory(`mp-breakdown-menu`, BreakdownMenu);
